/* Buy Haggis calculator v2.0.0: 1–1,000 guests, 1.36kg rolls, optional extra Chieftain. */
(function () {
  'use strict';
  const MAX_GUESTS = 1000;
  const whole = (x, min = 0, max = MAX_GUESTS) => {
    const s = String(x).trim(), n = Number(s);
    if (!/^\d+$/.test(s) || !Number.isSafeInteger(n) || n < min || n > max)
      throw new Error(`Enter a whole number from ${min} to ${max}.`);
    return n;
  };
  const grams = n => n < 1000 ? `${n}g` : `${Number((n / 1000).toFixed(3))}kg`;
  const roundQuantity = (n, p) => n <= 0 ? 0 : Math.ceil(Math.max(n, p.min || 1) / (p.step || 1)) * (p.step || 1);
  const fits = (p, q) => !!p && !p.failed && p.available === true && Number.isSafeInteger(p.price) && p.price > 0 &&
    Number.isSafeInteger(q) && q >= (p.min || 1) && q <= p.cap && q % (p.step || 1) === 0;
  const eligible = (p, g) => p.kind === 'haggis' && p.format === 'roll' && p.grams === 1360 && p.diet === g.diet && p.gf === g.gf;
  const extrasEligible = (p, rows) => ['neeps', 'tatties', 'sauce'].includes(p.kind) && p.available && !p.failed &&
    rows.every(r => !r.count || ((!r.gf || p.gf) && (r.diet !== 'vegan' || p.vegan) && (r.diet !== 'vegetarian' || p.vegetarian)));
  const chieftainEligible = rows => rows.some(r => r.diet === 'traditional' && r.count > r.gf);
  function makeGroups(total, rows) {
    whole(total, 1);
    rows.forEach(r => {
      whole(r.count); whole(r.gf);
      if (!['traditional', 'vegetarian', 'vegan'].includes(r.diet)) throw new Error('Choose a haggis type.');
      if (r.gf > r.count) throw new Error('Gluten-free guests are included in each group, not additional guests.');
    });
    if (rows.reduce((n, r) => n + r.count, 0) !== total) throw new Error('The group counts must add up to the total number of guests.');
    const groups = new Map();
    rows.forEach(r => {
      [[false, r.count - r.gf], [true, r.gf]].forEach(([gf, count]) => {
        if (!count) return;
        const diet = r.diet === 'traditional' ? 'traditional' : 'plant', key = `${diet}-${gf}`;
        if (!groups.has(key)) groups.set(key, { key, diet, gf, count: 0 });
        groups.get(key).count += count;
      });
    });
    return Array.from(groups.values());
  }
  function planRolls(products, group, portion) {
    if (![125, 200, 225].includes(portion)) throw new Error('Choose a serving size.');
    const target = group.count * portion;
    const product = products.find(p => eligible(p, group));
    // Keep the required quantity visible even when stock is insufficient; never reduce servings.
    const quantity = roundQuantity(Math.ceil(target / 1360), product || {});
    return { ...group, target, product, quantity, weight: quantity * 1360,
      price: product && !product.failed && Number.isSafeInteger(product.price) ? quantity * product.price : null,
      orderable: fits(product, quantity) };
  }
  const quantities = cart => {
    const map = new Map();
    (cart.items || []).forEach(i => map.set(Number(i.variant_id), (map.get(Number(i.variant_id)) || 0) + Number(i.quantity)));
    return map;
  };
  const api = { MAX_GUESTS, whole, grams, roundQuantity, fits, eligible, extrasEligible, chieftainEligible, makeGroups, planRolls };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('bh-haggis-calculator')) return;
  class HaggisCalculator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const template = this.querySelector('template'), data = this.querySelector('[data-bh-catalog]');
      if (!template || !data) return;
      try { this.config = JSON.parse(data.textContent); } catch (_) { return; }
      this.ready = true;
      this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));
      const fallback = this.querySelector('[data-bh-fallback]'); if (fallback) fallback.hidden = true;
      this.ui = this.shadowRoot; this.form = this.ui.querySelector('form');
      this.status = this.ui.querySelector('[role=status]'); this.results = this.ui.querySelector('[data-results]');
      this.root = this.config.root.endsWith('/') ? this.config.root : `${this.config.root}/`;
      this.money = n => new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.currency || this.config.currency }).format(n / 100);
      this.form.addEventListener('submit', e => { e.preventDefault(); this.calculate(); });
      this.form.addEventListener('input', () => this.invalidate());
      this.form.addEventListener('change', () => { this.toggleGroups(); this.invalidate(); });
      this.toggleGroups();
    }
    el(tag, text, className) { const n = document.createElement(tag); if (text !== undefined) n.textContent = text; if (className) n.className = className; return n; }
    field(name) { return this.form.elements.namedItem(name); }
    say(text, error = false) { this.status.textContent = text; this.status.className = error ? 'status error' : 'status'; }
    link(title, href) { const a = this.el('a', title); a.href = href; return a; }
    toggleGroups() {
      const mixed = this.field('party').value === 'mixed';
      this.ui.querySelector('[data-single]').hidden = mixed;
      this.ui.querySelector('[data-mixed]').hidden = !mixed;
    }
    invalidate() { if (!this.busy && !this.uncertain) { this.results.hidden = true; this.complete = false; this.say(''); } }
    async get(path, options = {}) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        let body; try { body = await response.json(); } catch (_) { throw new Error('The shop returned an unreadable response.'); }
        if (!response.ok) throw new Error(typeof body.description === 'string' ? body.description : 'The shop could not complete this request.');
        return body;
      } finally { clearTimeout(timer); }
    }
    read() {
      const total = whole(this.field('guests').value, 1), portion = Number(this.field('portion').value);
      if (![125, 200, 225].includes(portion)) throw new Error('Choose a serving size.');
      const rows = this.field('party').value === 'mixed'
        ? ['traditional', 'vegetarian', 'vegan'].map(diet => ({ diet, count: whole(this.field(diet).value), gf: whole(this.field(`${diet}-gf`).value) }))
        : [{ diet: this.field('diet').value, count: total, gf: this.field('gf').checked ? total : 0 }];
      return { total, portion, rows, groups: makeGroups(total, rows) };
    }
    setBusy(busy) {
      this.busy = busy;
      this.ui.querySelector('[data-inputs]').disabled = busy || !!this.uncertain;
      if (this.addButton) this.addButton.disabled = busy || !!this.uncertain || !this.complete;
    }
    async refresh(records, cart) {
      const existing = quantities(cart);
      return Promise.all(records.map(async record => {
        try {
          const product = await this.get(`${this.root}products/${encodeURIComponent(record.handle)}.js`);
          const variant = product.variants.find(v => Number(v.id) === record.id);
          if (!variant) throw new Error('The configured variant is no longer available.');
          const rule = variant.quantity_rule || {};
          const stock = record.stock === null ? Number.MAX_SAFE_INTEGER : Math.max(0, Number(record.stock));
          const ruleMax = rule.max === undefined ? record.max : rule.max;
          const maximum = ruleMax == null ? Number.MAX_SAFE_INTEGER : Math.max(0, Number(ruleMax));
          const price = Number(variant.price);
          if (!Number.isSafeInteger(price) || price <= 0 || !Number.isSafeInteger(stock) || !Number.isSafeInteger(maximum)) throw new Error('Invalid product data.');
          return { ...record, title: product.title, price, available: variant.available === true && !product.requires_selling_plan,
            min: Number(rule.min) || record.min || 1, step: Number(rule.increment) || record.step || 1,
            cap: Math.max(0, Math.min(stock, maximum) - (existing.get(record.id) || 0)), failed: false };
        } catch (_) { return { ...record, available: false, cap: 0, failed: true }; }
      }));
    }
    async calculate() {
      if (this.busy || this.uncertain) return;
      this.results.hidden = true; this.complete = false;
      try { this.plan = this.read(); } catch (e) { this.say(e.message, true); return; }
      this.setBusy(true); this.say('Checking catering rolls, Chieftain options and current prices…');
      try {
        const cart = await this.get(`${this.root}cart.js`);
        this.currency = cart.currency || this.config.currency;
        this.products = await this.refresh(this.config.products, cart);
        this.groups = this.plan.groups.map(g => planRolls(this.products, g, this.plan.portion));
        this.complete = this.groups.every(g => g.orderable);
        this.render();
        const failed = this.products.some(p => p.failed);
        this.say(failed ? 'Some products could not be checked. Unchecked products cannot be added; your roll quantities are still shown.' : 'Your catering quantities are ready.', failed);
        this.ui.querySelector('[data-result-title]').focus();
      } catch (_) { this.say('We could not check the shop just now. Nothing was added. Please try again or use the portion guide below.', true); }
      finally { this.setBusy(false); }
    }
    render() {
      this.results.replaceChildren(); this.results.hidden = false;
      const heading = this.el('h3', `Your ${this.plan.total.toLocaleString('en-GB')}-person ${this.plan.portion === 125 ? 'starter' : 'supper'}`);
      heading.tabIndex = -1; heading.setAttribute('data-result-title', ''); this.results.append(heading);
      this.results.append(this.el('p', `Haggis target: ${grams(this.plan.total * this.plan.portion)}, at ${this.plan.portion}g per person. Calculated using 1.36kg catering rolls only, rounded up separately for each dietary group.`, 'summary'));
      for (const g of this.groups) {
        const box = this.el('fieldset', undefined, 'group');
        const name = `${g.gf ? 'Gluten-free ' : ''}${g.diet === 'plant' ? 'vegetarian / vegan' : 'traditional'}`;
        box.append(this.el('legend', `${g.count} × ${name} · target ${grams(g.target)}`));
        const card = this.el('div', undefined, 'choice roll');
        card.append(this.el('strong', `${g.quantity} × 1.36kg catering roll${g.quantity === 1 ? '' : 's'}`));
        card.append(this.el('p', `${grams(g.weight)} total · ${grams(g.weight - g.target)} above this group’s target.`, 'small'));
        if (g.product) {
          card.append(this.link(g.product.title, `${this.root}products/${g.product.handle}?variant=${g.product.id}`));
          card.append(this.el('p', g.price === null ? 'Price could not be checked.' : `Roll subtotal: ${this.money(g.price)}`));
          card.append(this.el('p', g.gf ? 'Meat haggis with gluten-free oats. Not vegetarian or vegan.' : g.diet === 'plant' ? 'Suitable for vegetarians and vegans. Contains oats; may contain wheat. Not a gluten-free selection.' : 'Traditional meat haggis. Contains oats; may contain wheat. Not a gluten-free selection.', 'small'));
        }
        if (!g.orderable) {
          card.append(this.el('p', g.product ? 'The full calculated quantity cannot currently be added. We have not reduced the portions or substituted smaller packs.' : 'No matching 1.36kg roll is configured for these requirements. The quantity above is for planning only, not a suitable product recommendation.', 'error'));
          card.append(this.link('Contact us about this catering quantity', `${this.root}pages/about-us`));
        }
        box.append(card); this.results.append(box);
      }
      this.renderChieftain();
      this.extras = [];
      const extraBox = this.el('fieldset', undefined, 'group'); extraBox.append(this.el('legend', 'Neeps, tatties and sauce — optional'));
      extraBox.append(this.el('p', 'Extras must match the whole group’s selected requirements. Nothing is preselected. Suggested quantities are full side portions, even when haggis is a starter.', 'small'));
      const extras = this.products.filter(p => extrasEligible(p, this.plan.rows));
      for (const p of extras) {
        const quantity = roundQuantity(Math.ceil(this.plan.total * p.portion / p.grams), p);
        const row = this.el('div', undefined, 'extra'), label = this.el('label', undefined, 'choice-label');
        const checkbox = this.el('input'); checkbox.type = 'checkbox';
        label.append(checkbox, this.el('strong', `${p.kind === 'neeps' ? 'Neeps' : p.kind === 'tatties' ? 'Tatties' : 'Whisky cream sauce'} · ${this.money(p.price)} per pack`));
        row.append(label, this.link(p.title, `${this.root}products/${p.handle}`), this.el('p', `${grams(p.grams)} pack. ${p.note}`, 'small'));
        const qtyLabel = this.el('label', 'Packs to add ', 'quantity'), input = this.el('input');
        input.type = 'number'; input.min = p.min; input.max = p.cap; input.step = p.step; input.value = quantity; input.inputMode = 'numeric';
        qtyLabel.append(input); row.append(qtyLabel);
        row.append(this.el('p', `Suggested: ${quantity} packs, allowing ${p.portion}g per person. ${p.kind === 'sauce' ? 'Based on our supper recipe.' : 'Based on the maker’s two-serving pack.'}`, 'small'));
        if (!fits(p, quantity)) {
          checkbox.disabled = true; input.disabled = true;
          row.append(this.el('p', 'The full suggested quantity cannot currently be added. Contact us for your function; no smaller quantity has been selected automatically.', 'error'));
        }
        this.extras.push({ product: p, checkbox, input });
        checkbox.addEventListener('change', () => this.updateTotal()); input.addEventListener('input', () => this.updateTotal());
        extraBox.append(row);
      }
      if (!extras.length) extraBox.append(this.el('p', 'No checked ready-made extras match the whole table. The stocked mash contains dairy; sauce is not offered for vegan or gluten-free guests.'));
      extraBox.append(this.link('Recipe and plant-based mash alternatives', `${this.root}blogs/news/haggis-neeps-and-tatties-recipe`));
      this.results.append(extraBox);
      this.total = this.el('p', '', 'total'); this.total.setAttribute('aria-live', 'polite'); this.results.append(this.total);
      this.weightSummary = this.el('p', '', 'small'); this.results.append(this.weightSummary);
      this.results.append(this.el('p', 'Product subtotal only, excluding existing basket items. Delivery, discounts and final availability are confirmed in the basket and checkout. For large functions, contact us to confirm supply and delivery arrangements.', 'small'));
      this.addButton = this.el('button', 'Add selected items to basket', 'primary'); this.addButton.type = 'button';
      this.addButton.addEventListener('click', () => this.add()); this.results.append(this.addButton, this.link('View basket', this.config.cart));
      if (!this.complete) this.results.append(this.el('p', 'Basket addition is paused because at least one dietary group cannot be fully supplied by the checked 1.36kg rolls. Contact us to arrange the full order.', 'error'));
      this.updateTotal();
    }
    renderChieftain() {
      const box = this.el('fieldset', undefined, 'group'); box.append(this.el('legend', 'Add a ceremonial Chieftain — optional'));
      box.append(this.el('p', 'Choose one extra presentation haggis. This is additional to the catering rolls and does not reduce their calculated quantity.', 'small'));
      const label = this.el('label', 'Choose your Chieftain'), select = this.el('select');
      select.setAttribute('data-chieftain', ''); select.name = 'chieftain';
      const none = this.el('option', 'No Chieftain — catering rolls only'); none.value = ''; select.append(none);
      const allowed = chieftainEligible(this.plan.rows);
      this.products.filter(p => p.kind === 'chieftain').sort((a, b) => a.grams - b.grams).forEach(p => {
        const ready = fits(p, 1), price = p.failed ? 'price unavailable' : this.money(p.price);
        const option = this.el('option', `${p.title} — ${price}${ready ? '' : ' — unavailable'}`);
        option.value = String(p.id); option.disabled = !ready || !allowed; select.append(option);
      });
      label.append(select); box.append(label); select.disabled = !allowed;
      box.append(this.el('p', allowed ? 'For traditional, non-gluten-free guests only. Keep it separate from vegetarian, vegan and gluten-free meals, and check the selected product’s full allergen information.' : 'The Chieftains listed are meat products and are not offered as gluten-free. They cannot be added for an entirely vegetarian, vegan or gluten-free group.', 'small'));
      this.chieftainSelect = select; this.chieftainSummary = this.el('p', 'No extra Chieftain selected.', 'small');
      box.append(this.chieftainSummary); this.results.append(box);
      select.addEventListener('change', () => this.updateTotal());
    }
    chosenChieftain() {
      const id = this.chieftainSelect.value;
      if (!id) return null;
      const p = this.products.find(p => p.kind === 'chieftain' && String(p.id) === id);
      if (!chieftainEligible(this.plan.rows) || !fits(p, 1)) throw new Error('That Chieftain cannot currently be added. Choose another size or no Chieftain.');
      return p;
    }
    selection() {
      const lines = new Map();
      const put = (p, q) => { const old = lines.get(p.id); if (old) old.quantity += q; else lines.set(p.id, { product: p, quantity: q }); };
      if (!this.groups.every(g => g.orderable)) throw new Error('Full catering quantity unavailable — contact us to arrange your order.');
      this.groups.forEach(g => put(g.product, g.quantity));
      const chieftain = this.chosenChieftain(); if (chieftain) put(chieftain, 1);
      this.extras.filter(x => x.checkbox.checked).forEach(x => {
        const q = whole(x.input.value, x.product.min, x.product.cap);
        if (!fits(x.product, q)) throw new Error('Check the pack quantities for your extras.');
        put(x.product, q);
      });
      const result = Array.from(lines.values());
      if (result.some(l => !fits(l.product, l.quantity))) throw new Error('The full quantity cannot be added. Please recalculate or contact us.');
      return result;
    }
    updateTotal() {
      try {
        const chieftain = this.chosenChieftain();
        this.chieftainSummary.replaceChildren();
        if (chieftain) this.chieftainSummary.append(this.el('span', '1 × '), this.link(chieftain.title, `${this.root}products/${chieftain.handle}`), this.el('span', ` — ${this.money(chieftain.price)} extra. Catering roll quantities are unchanged.`));
        else this.chieftainSummary.textContent = 'No extra Chieftain selected.';
        const lines = this.selection(), rollWeight = this.groups.reduce((n, g) => n + g.weight, 0);
        this.total.textContent = `Selected product subtotal: ${this.money(lines.reduce((n, l) => n + l.product.price * l.quantity, 0))}`;
        this.weightSummary.textContent = `Catering rolls: ${grams(rollWeight)}${chieftain ? ` + extra Chieftain: ${grams(chieftain.grams)} = ${grams(rollWeight + chieftain.grams)} of haggis in total.` : '. No additional Chieftain.'}`;
        this.addButton.disabled = !this.complete || !!this.busy || !!this.uncertain;
      } catch (e) { this.total.textContent = e.message; this.weightSummary.textContent = ''; this.addButton.disabled = true; }
    }
    async add() {
      if (this.busy || this.uncertain || !this.complete) return;
      let lines; try { lines = this.selection(); } catch (e) { this.say(e.message, true); return; }
      this.setBusy(true); this.results.querySelectorAll('input,select').forEach(i => i.disabled = true);
      this.say('Rechecking quantities and prices before adding…');
      let submitted = false;
      try {
        const cart = await this.get(`${this.root}cart.js`), before = quantities(cart);
        const fresh = await this.refresh(lines.map(l => l.product), cart);
        if (cart.currency !== this.currency || fresh.some((p, i) => !fits(p, lines[i].quantity) || p.price !== lines[i].product.price))
          throw new Error('Prices, availability or your basket changed. Please recalculate before adding.');
        submitted = true;
        await this.get(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map(l => ({ id: l.product.id, quantity: l.quantity, properties: { _bh_calculator: 'v2' } })) }) });
        const after = quantities(await this.get(`${this.root}cart.js`));
        if (lines.some(l => (after.get(l.product.id) || 0) < (before.get(l.product.id) || 0) + l.quantity))
          throw new Error('The full quantity was not confirmed in the basket.');
        this.say('The full selection was added. Opening your basket…'); window.location.assign(this.config.cart);
      } catch (e) {
        if (submitted) {
          this.uncertain = true;
          this.say('We could not confirm the full basket update. Some items may have been added. Open “View basket” to check before trying again; this button is paused to prevent duplicates.', true);
        } else { this.say(e.message || 'Nothing was added. Please recalculate and try again.', true); this.results.hidden = true; }
        this.setBusy(false);
      }
    }
  }
  customElements.define('bh-haggis-calculator', HaggisCalculator);
}());
